import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import {
  AlertCardComponent,
  AlertType,
  DialogSkeletonComponent,
  FormErrorComponent,
  idKey,
  MatButtonLoadingDirective,
  MultiFileUploaderComponent,
  nameKey,
  SnackbarService,
  UploadedFileType,
} from '@tiltgroup/web-components';

import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { finalize } from 'rxjs';
import { LighthouseQueriesService } from '@services/queries/services/lighthouse-queries.service';
import { MatDialogClose, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';

type ImportQuoteForm = {
  documents: FormControl<File[] | null>;
  isQuickQuote: FormControl<boolean | null>;
};

@Component({
  selector: 'nx-import-quote-modal',
  imports: [
    DialogSkeletonComponent,
    MatButtonModule,
    MatButtonLoadingDirective,
    MatInputModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatOptionModule,
    MatSelectModule,
    FormErrorComponent,
    AlertCardComponent,
    MatIconModule,
    MatDialogClose,
    MultiFileUploaderComponent,
  ],
  templateUrl: './import-quote-modal.component.html',
  styleUrl: './import-quote-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImportQuoteModalComponent {
  private formBuilder = inject(FormBuilder);
  private queriesService = inject(LighthouseQueriesService);
  private dialogRef = inject(MatDialogRef);
  private snackbarService = inject(SnackbarService);

  currentFile: File | null = null;

  busy = signal<boolean>(false);

  protected readonly AlertType = AlertType;
  protected readonly idKey = idKey;
  protected readonly nameKey = nameKey;

  form: FormGroup<ImportQuoteForm>;

  acceptedFormats: UploadedFileType[] = [
    UploadedFileType.PDF,
    UploadedFileType.PNG,
    UploadedFileType.JPG_JPEG,
    UploadedFileType.TXT,
    UploadedFileType.CSV,
  ];

  maxFileSize = 1024 * 1024 * 10; // 10 MB in bytes

  constructor() {
    this._createUploadForm();
  }

  importQuote() {
    if (!this.form.valid) {
      return;
    }

    this.busy.set(true);

    const value = this.form.value;

    this.queriesService
      .importLtlQuotes(value.documents, value.isQuickQuote)
      .pipe(finalize(() => this.busy.set(false)))
      .subscribe({
        next: (res) => {
          this.dialogRef.close(res);
          this.snackbarService.showNotification(
            value.documents.length === 1
              ? 'LTL quote has been imported. It will appear in the list as a draft once processing is complete.'
              : 'LTL quotes have been imported. They will appear in the list as drafts once processing is complete.'
          );
        },
      });
  }

  private _createUploadForm() {
    const documentsControl = this.formBuilder.control<File[] | null>(
      null,
      Validators.required
    );

    this.form = this.formBuilder.group({
      documents: documentsControl,
      isQuickQuote: this.formBuilder.control<boolean | null>(
        true,
        Validators.required
      ),
    });
  }
}
